import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../helpers';
import { downloadBlob } from '../../../helpers/downloadHelper';
import {
  EvalFilters,
  exportMyEvaluations,
  getMyEvaluations,
} from '../../../requests/customerPortal';
import {
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
} from '../../Admin/ui';
import {
  dateInputClass,
  fmtDate,
  fmtNum,
  Stat,
  LoadFailed,
  retryUnless4xx,
} from '../shared';

const score = (v: number | null, max: number) =>
  v == null ? (
    '—'
  ) : (
    <>
      {fmtNum(v, 1)}
      <span className="ml-1 text-sm font-normal text-gray-bold">/ {max}</span>
    </>
  );

const AdminEvaluations: React.FC = () => {
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const filters: EvalFilters = { type, from, to, page };
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'evaluations', filters],
    queryFn: () => getMyEvaluations(filters),
    placeholderData: (prev) => prev,
  });

  const onExport = async () => {
    setExporting(true);
    try {
      downloadBlob(
        await exportMyEvaluations({ type, from, to }),
        'evaluations.csv',
      );
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Evaluations"
        meta="Ratings and participant takeaways from your workshops."
        actions={
          <button
            className={btnSecondary}
            onClick={onExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      />
      <PageBody>
        <Toolbar>
          <select
            className={inputClass}
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Public and private</option>
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </select>
          <label className="text-xs text-gray-bold">Course date</label>
          <input
            type="date"
            className={dateInputClass}
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
          <span className="text-xs text-gray-two">to</span>
          <input
            type="date"
            className={dateInputClass}
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </Toolbar>

        {isLoading ? (
          <LoadingSpinner />
        ) : error || !data ? (
          <LoadFailed error={error} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
              <Stat
                label="Overall course rating"
                value={score(data.kpis.courseRating, 5)}
              />
              <Stat
                label="Recommend Learnit"
                value={score(data.kpis.recommend, 10)}
                caption={
                  data.kpis.recommend != null
                    ? `${Math.round(data.kpis.recommend * 10)}%`
                    : undefined
                }
              />
              <Stat
                label="Instructor rating"
                value={score(data.kpis.instructorRating, 5)}
              />
              <Stat
                label="Learner knowledge"
                value={score(data.kpis.learnerKnowledge, 5)}
              />
              <Stat
                label="Course material"
                value={score(data.kpis.courseMaterial, 5)}
              />
              <Stat label="Evaluations" value={fmtNum(data.kpis.count)} />
            </div>

            <Card className="mt-6">
              {data.items.length === 0 ? (
                <EmptyState title="No evaluations in this window" />
              ) : (
                <table className={tableClass}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Course</th>
                      <th className={thClass}>Course date</th>
                      <th className={thClass}>Type</th>
                      <th className={thClass}>Attendee</th>
                      <th className={`${thClass} text-right`}>Rating</th>
                      <th className={thClass}>Top takeaways</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r) => (
                      <tr key={r.id}>
                        <td className={tdClass}>{r.course ?? '—'}</td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {fmtDate(r.date)}
                        </td>
                        <td className={tdClass}>
                          <Pill value={r.type} tone="neutral" />
                        </td>
                        <td className={tdClass}>{r.attendee ?? '—'}</td>
                        <td className={`${tdClass} text-right`}>
                          {fmtNum(r.courseRating)}
                        </td>
                        <td className={`${tdClass} max-w-md text-gray-three`}>
                          {r.takeaways ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination
                page={data.page}
                pages={Math.ceil(data.total / data.pageSize)}
                total={data.total}
                onPage={setPage}
              />
            </Card>
            <p className="mt-3 text-xs text-gray-bold">
              Figures are averages across your organisation's evaluations for
              the selected window.
            </p>
          </>
        )}
      </PageBody>
    </>
  );
};

export default AdminEvaluations;
